import {
  ArticlesByTagTemplate,
  ArticlesByTagTemplateProps,
} from '@/components/template/ArticlesByTagTemplate';
import { mapBlogResponseToBlogPreview } from '@/lib/blog';
import {
  extractArticleTags,
  slugifyTag,
  tagSlugToSearchTerm,
} from '@/lib/newsTags';
import { getBlogPosts } from '@/services/wordpress/articles';
import { getCategories } from '@/services/wordpress/categories';
import { RawBlogType } from '@/types/blog';
import { BasePageProps } from '@/types/page';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import { ParsedUrlQuery } from 'querystring';

const MAX_TAG_ARTICLES = 20;

const toTitleCase = (value: string) =>
  value.replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Article tags only exist as a "Tags: …" line in the WordPress content (no
 * tag taxonomy on the `articles` post type), so this page full-text searches
 * WordPress for the tag and lists articles actually tagged with it first.
 */
export const getStaticProps: GetStaticProps<
  ArticlesByTagTemplateProps,
  ParsedUrlQuery
> = async (context) => {
  const { slug: rawSlug } = context.params || {};
  const slug = typeof rawSlug === 'string' ? slugifyTag(rawSlug) : '';
  const searchTerm = tagSlugToSearchTerm(slug);

  if (!slug || !searchTerm || slug.length > 80) {
    return { notFound: true, revalidate: 60 };
  }

  if (rawSlug !== slug) {
    return {
      redirect: { destination: `/news/tag/${slug}`, permanent: true },
      revalidate: 600,
    };
  }

  const [categories, searchResponse] = await Promise.all([
    getCategories({ fields: ['id', 'name', 'slug'] }).catch(() => []),
    getBlogPosts({
      fields: ['id', 'title', 'excerpt', 'slug', 'content'],
      getCategoriesAndFeaturedImage: true,
      search: searchTerm,
      per_page: MAX_TAG_ARTICLES,
    }).catch(() => undefined),
  ]);

  let rawArticles: RawBlogType[] = [];
  if (searchResponse?.ok) {
    const json = await searchResponse.json();
    rawArticles = Array.isArray(json) ? json : [];
  }

  let tagName = '';
  const tagged: RawBlogType[] = [];
  const mentioned: RawBlogType[] = [];
  rawArticles.forEach((article) => {
    const exactTag = extractArticleTags(article.content?.rendered ?? '').find(
      (tag) => slugifyTag(tag) === slug
    );
    if (exactTag) {
      tagName = tagName || exactTag;
      tagged.push(article);
    } else {
      mentioned.push(article);
    }
  });

  const articles = mapBlogResponseToBlogPreview(
    [...tagged, ...mentioned].map((article) => ({
      ...article,
      content: { ...article.content, rendered: '' },
    }))
  );
  const name = tagName || toTitleCase(searchTerm);

  return {
    props: {
      tag: { name, slug },
      categories,
      articles,
      metadata: {
        title: `#${name} news | EV.com`,
        description: `The latest electric vehicle news, reviews and analysis tagged #${name} on EV.com.`,
        // Keep empty tag pages out of the index; populated ones are indexable.
        noindex: articles.length === 0,
      },
    } satisfies BasePageProps<ArticlesByTagTemplateProps>,
    revalidate: 600,
  };
};

export default function NewsTagPage(
  props: InferGetStaticPropsType<typeof getStaticProps>
) {
  return (
    <main>
      <ArticlesByTagTemplate {...props} />
    </main>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};
